import { CloseButton } from '@components/utility/buttons';
import { COLLEAGUE_HASH_KEY, GUEST_ACCESS_HASH_KEY } from '@event/constants';
import { useAddNonParticipantMutation } from '@event/services/events';
import { EventTypes } from '@event/types/event';
import CheckIcon from '@mui/icons-material/Check';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseIcon from '@mui/icons-material/Close';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogProps,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import getBringColleagueSchema from '@schemas/BringColleague';
import { handleError } from '@utils/common';
import { toLogin } from '@utils/route';
import { useFormik } from 'formik';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useParams, useSearchParams } from 'react-router-dom';

const RegistrationSuccessfullySent = () => (
    <div className="flex flex-col items-center max-w-[365px] text-center gap-3">
        <div className="flex items-center justify-center size-24 rounded-full border-2">
            <CheckRoundedIcon className="size-10" />
        </div>
        <Typography fontSize={53} fontFamily="museo-sans">
            <FormattedMessage id="common.success" defaultMessage="Success" />
        </Typography>
        <Typography fontSize={26}>
            <FormattedMessage
                id="event.colleague.success"
                defaultMessage="Your information has been submitted. Please wait for the approval from the event organizer."
            />
        </Typography>
    </div>
);

const ColleagueDialog = (props: DialogProps) => {
    const [showSuccess, setShowSuccess] = useState(false);

    const [searchParams, setSearchParams] = useSearchParams();
    const { type, eventId } = useParams();
    const intl = useIntl();
    const colleagueHash = useMemo(() => searchParams.get(COLLEAGUE_HASH_KEY), [searchParams]);
    const eventType = type as EventTypes;

    const [addNonParticipant, { isLoading }] = useAddNonParticipantMutation();

    const { handleSubmit, errors, touched, resetForm, values, handleChange, handleBlur } = useFormik({
        initialValues: {
            first_name: '',
            last_name: '',
            email: '',
            company_name: '',
        },
        validationSchema: getBringColleagueSchema(intl),
        onSubmit: async (values) => {
            try {
                const { guest_access_hash } = await addNonParticipant({
                    id: Number(eventId),
                    type: eventType,
                    colleagueHash,
                    ...values,
                }).unwrap();
                setSearchParams((prev) => {
                    const params = new URLSearchParams(prev);
                    params.set(GUEST_ACCESS_HASH_KEY, guest_access_hash);
                    return params;
                });
                resetForm();
                setShowSuccess(true);
            } catch (err) {
                handleError(
                    intl.formatMessage({
                        id: 'event.error.registration',
                        defaultMessage: 'Error registering for event',
                    }),
                    err
                );
            }
        },
    });

    const handleClose = () => {
        props.onClose?.({}, 'escapeKeyDown');
        setTimeout(() => {
            resetForm();
            setShowSuccess(false);
        }, 300);
    };
    return (
        <Dialog
            {...props}
            disableEscapeKeyDown
            onClose={handleClose}
            slotProps={{
                paper: {
                    className:
                        'flex rounded-lg items-center mx-4 min-w-0 pt-16 pb-6 px-2 sm:max-w-[630px] sm:px-12 md:overflow-hidden',
                },
            }}
        >
            <CloseButton className="size-10 flex" color="black" onClick={handleClose} />
            {!showSuccess ? (
                <>
                    <DialogTitle className="w-full px-2 mx-auto text-center max-w-[523px]">
                        <Typography className="text-xl mb-4 md:text-2xl" fontWeight={700}>
                            <FormattedMessage
                                id="event.missingDetails.title"
                                defaultMessage="Fill in your information"
                            />
                        </Typography>
                        <Typography className="w-full mb-5 whitespace-break-spaces" fontWeight={500}>
                            <FormattedMessage
                                id="event.missingDetails.description"
                                defaultMessage="In order to get you on the guest list for the event you've been invited for, we need some information about you."
                            />
                        </Typography>
                        <Typography
                            className="w-full flex gap-1 justify-center whitespace-nowrap"
                            fontWeight={600}
                            fontSize={12}
                        >
                            <FormattedMessage
                                id="common.alreadyHaveAccount"
                                defaultMessage="Already have a Victron Professional account?"
                            />
                            <Typography
                                component={Link}
                                color="primary"
                                fontSize={12}
                                fontWeight={600}
                                className="cursor-pointer"
                                to={toLogin()}
                            >
                                <FormattedMessage id="common.logIn" defaultMessage="Log in" />
                            </Typography>
                        </Typography>
                    </DialogTitle>
                    <DialogContent className="w-full p-0 mt-5 max-w-[523px] text-center sm:py-2">
                        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                            <TextField
                                className="mt-2 md:mt-0"
                                size="small"
                                label={<FormattedMessage id="common.firstName" defaultMessage="First Name" />}
                                fullWidth
                                name="first_name"
                                value={values.first_name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.first_name && !!errors.first_name}
                                helperText={touched.first_name && errors.first_name}
                            />
                            <TextField
                                size="small"
                                label={<FormattedMessage id="common.lastName" defaultMessage="Last Name" />}
                                fullWidth
                                name="last_name"
                                value={values.last_name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.last_name && !!errors.last_name}
                                helperText={touched.last_name && errors.last_name}
                            />
                            <TextField
                                size="small"
                                label={<FormattedMessage id="common.email" defaultMessage="Email" />}
                                fullWidth
                                name="email"
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.email && !!errors.email}
                                helperText={touched.email && errors.email}
                            />
                            <TextField
                                size="small"
                                label={<FormattedMessage id="common.company" defaultMessage="Company" />}
                                fullWidth
                                name="company_name"
                                value={values.company_name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.company_name && !!errors.company_name}
                                helperText={touched.company_name && errors.company_name}
                            />
                            <div className="flex flex-col gap-2 mt-5 md:gap-4 md:mb-6 md:flex-row">
                                <Button
                                    fullWidth
                                    disableElevation={false}
                                    color="neutral-650"
                                    className="rounded"
                                    startIcon={<CloseIcon />}
                                    onClick={handleClose}
                                >
                                    <span>
                                        <FormattedMessage id="common.cancel" defaultMessage="CANCEL" />
                                    </span>
                                </Button>
                                <Button
                                    type="submit"
                                    fullWidth
                                    disableElevation={false}
                                    className="rounded"
                                    disabled={isLoading}
                                    startIcon={isLoading ? <CircularProgress size={20} color="white" /> : <CheckIcon />}
                                >
                                    <span>
                                        <FormattedMessage id="common.submit" defaultMessage="SUBMIT" />
                                    </span>
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </>
            ) : (
                <RegistrationSuccessfullySent />
            )}
        </Dialog>
    );
};

export { ColleagueDialog };
